import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, CheckCircle2, FolderOpen, Images, Loader2, X } from 'lucide-react';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { storage } from '../../lib/firebase/config';
import {
  choosePhotoFolder,
  folderPermission,
  looksLikeSkuFolder,
  PHOTO_FOLDER_NAME,
  photoFolderSupported,
  readSkuPhotos,
  refreshPhotoFolder,
  resizeForListing,
  savedPhotoFolder,
  type SkuPhotos,
} from '../../lib/claire/photoFolder';
import { ClaireService, type ClaireFolderJob } from '../../services/claireService';
import { cn } from '../../utils/cn';

/** Jobs older than this wait for a click instead of reading the folder as soon as the chat opens. */
const AUTO_READ_MS = 15 * 60 * 1000;

const errText = (e: any) => e?.message?.replace(/^.*?:\s*/, '') || 'Something went wrong.';

/**
 * Reads the requested SKU folders from the photo folder on this computer, uploads the photos the
 * way the Product Entry page does, lets Claire review them, and saves them to the entries on confirm.
 */
export const FolderPhotosCard: React.FC<{ jobId: string; compact?: boolean; onFollowUp?: (text: string) => void }> = ({ jobId, compact, onFollowUp }) => {
  const [job, setJob] = useState<ClaireFolderJob | null>(null);
  const [folder, setFolder] = useState<Awaited<ReturnType<typeof savedPhotoFolder>>>(null);
  const [perm, setPerm] = useState<'granted' | 'denied' | 'prompt' | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [matched, setMatched] = useState<SkuPhotos[]>([]);
  const [notFound, setNotFound] = useState<string[]>([]);
  const [skipped, setSkipped] = useState<string[]>([]);
  const [mode, setMode] = useState<'add' | 'replace'>('add');
  const [error, setError] = useState<string | null>(null);
  const [wrongFolder, setWrongFolder] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    let live = true;
    ClaireService.folderPhotos('get', jobId)
      .then((j) => live && setJob(j))
      .catch((e) => live && setError(errText(e)));
    savedPhotoFolder().then(async (h) => {
      if (!live || !h) return;
      setFolder(h);
      setPerm(await folderPermission(h, false));
    });
    return () => {
      live = false;
    };
  }, [jobId]);

  const read = useCallback(
    async (root: NonNullable<typeof folder>) => {
      if (!job) return;
      started.current = true;
      setError(null);
      try {
        const wanted = job.skus.filter((s) => s.entry);
        refreshPhotoFolder(root);
        const found: SkuPhotos[] = [];
        for (const s of wanted) {
          setProgress(`Searching “${root.name}” for ${s.sku}…`);
          found.push(await readSkuPhotos(root, s.sku));
        }
        setMatched(found);
        setNotFound(found.filter((f) => !f.found).map((f) => f.sku));
        setSkipped(found.flatMap((f) => f.skipped.map((n) => `${f.sku}/${n}`)));
        const total = found.reduce((n, f) => n + f.files.length, 0);
        if (!total) {
          setProgress(null);
          const emptyFolders = found.filter((f) => f.found);
          if (!emptyFolders.length && looksLikeSkuFolder(root.name)) {
            setWrongFolder(true);
            setError(`“${root.name}” is a single SKU’s folder. Choose “${PHOTO_FOLDER_NAME}”, the folder that holds all the SKU folders; it’s remembered after that.`);
          } else {
            setError(
              emptyFolders.length
                ? `Found ${emptyFolders.map((f) => `“${f.folderName}”`).join(', ')} but no JPG, PNG or WebP photos inside.`
                : `No folder with ${wanted.length === 1 ? 'that SKU' : 'those SKUs'} in its name inside “${root.name}”. If the SKU folders are somewhere else, use a different folder.`,
            );
          }
          started.current = false;
          return;
        }
        setWrongFolder(false);
        const uploads: Record<string, string[]> = {};
        let done = 0;
        const stamp = Date.now();
        for (const f of found) {
          uploads[f.sku] = [];
          for (let i = 0; i < f.files.length; i++) {
            setProgress(`Uploading photo ${++done} of ${total}…`);
            const file = f.files[i];
            const name = `${f.sku}_${stamp}_${i}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_').replace(/\.(png|webp|jpeg)$/i, '.jpg')}`;
            const r = ref(storage, `product-images/${f.sku}/${name}`);
            await uploadBytes(r, await resizeForListing(file), { contentType: 'image/jpeg' });
            uploads[f.sku].push(await getDownloadURL(r));
          }
        }
        setProgress('Claire is looking over the photos…');
        const reviewed = await ClaireService.folderPhotos('review', jobId, { uploads });
        setJob(reviewed);
        // Save without waiting for a click, unless an entry already has photos (then add vs. replace is their call).
        const toSave = reviewed.skus.filter((s) => s.entry && s.uploads?.length);
        if (reviewed.status === 'review_ready' && toSave.length && !toSave.some((s) => (s.entry?.imageCount || 0) > 0)) {
          setProgress(reviewed.thenSubmit ? 'Saving the photos and sending to review…' : 'Saving the photos…');
          const next = await ClaireService.folderPhotos('attach', jobId, { mode: 'add' });
          setJob(next);
          const saved = next.skus.filter((s) => s.result).map((s) => s.sku);
          if (next.thenReview && saved.length && onFollowUp) onFollowUp(`Review ${saved.join(', ')} now and save your notes for the reviewer.`);
        }
        setProgress(null);
      } catch (e) {
        setProgress(null);
        setError(errText(e));
        started.current = false;
      }
    },
    [job, jobId, onFollowUp],
  );

  useEffect(() => {
    if (!job || job.status !== 'waiting' || !folder || perm !== 'granted' || started.current) return;
    const fresh = job.createdAt && Date.now() - new Date(job.createdAt).getTime() < AUTO_READ_MS;
    if (fresh) read(folder);
  }, [job, folder, perm, read]);

  const connect = async () => {
    setError(null);
    try {
      const h = await choosePhotoFolder();
      setFolder(h);
      setPerm('granted');
      read(h);
    } catch (e: any) {
      if (e?.name !== 'AbortError') setError(errText(e));
    }
  };

  const allow = async () => {
    if (!folder) return connect();
    const p = await folderPermission(folder, true).catch(() => 'denied' as const);
    setPerm(p);
    if (p === 'granted') read(folder);
  };

  const finish = async (action: 'attach' | 'cancel') => {
    setError(null);
    setProgress(action === 'attach' ? 'Saving to the product entries…' : 'Cancelling…');
    try {
      const next = await ClaireService.folderPhotos(action, jobId, action === 'attach' ? { mode } : {});
      setJob(next);
      const saved = next.skus.filter((s) => s.result).map((s) => s.sku);
      if (action === 'attach' && next.thenReview && saved.length && onFollowUp) {
        onFollowUp(`Review ${saved.join(', ')} now and save your notes for the reviewer.`);
      }
    } catch (e) {
      setError(errText(e));
    } finally {
      setProgress(null);
    }
  };

  const noEntry = job?.skus.filter((s) => !s.entry).map((s) => s.sku) || [];
  const ready = job?.skus.filter((s) => s.entry && s.uploads?.length) || [];
  const hasExisting = ready.some((s) => (s.entry?.imageCount || 0) > 0);
  const thumb = compact ? 48 : 60;

  const button = 'inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12.5px] font-medium disabled:opacity-50';
  const primary = cn(button, 'bg-[#241813] text-white hover:bg-black');
  const secondary = cn(button, 'border border-[#e0d6c6] bg-white text-[#5a4a3a] hover:border-[#b8986e]');

  return (
    <div className={cn('mt-2.5 rounded-xl border border-[#e0d3bf] bg-[#fbf7f0] px-3.5 py-3', compact ? 'text-[12.5px]' : 'text-[13.5px]')}>
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#9a8a78]">
        <Images className="w-3.5 h-3.5" /> Photos from the photo folder
      </p>

      {!job && !error && <Loader2 className="mt-2 w-4 h-4 animate-spin text-[#9a8a78]" />}

      {job?.status === 'waiting' && !progress && (
        <div className="mt-1.5 space-y-2 text-[#241813]">
          <p>
            For {job.skus.filter((s) => s.entry).map((s) => s.sku).join(', ') || 'no product entries'}
            {folder ? ` from “${folder.name}”` : ''}.
          </p>
          {!photoFolderSupported() ? (
            <p className="text-[12px] text-amber-800">This needs Chrome or Edge on a computer that has the photo folder.</p>
          ) : job.skus.some((s) => s.entry) ? (
            <div className="flex flex-wrap gap-2">
              {!folder || wrongFolder ? (
                <button onClick={connect} className={primary}>
                  <FolderOpen className="w-3.5 h-3.5" /> Choose “{PHOTO_FOLDER_NAME}”
                </button>
              ) : perm !== 'granted' ? (
                <button onClick={allow} className={primary}>
                  <FolderOpen className="w-3.5 h-3.5" /> Allow access to “{folder.name}”
                </button>
              ) : (
                <button onClick={() => read(folder)} className={primary}>
                  <Images className="w-3.5 h-3.5" /> Read photos
                </button>
              )}
              {folder && !wrongFolder && (
                <button onClick={connect} className={secondary}>
                  Use a different folder
                </button>
              )}
            </div>
          ) : null}
        </div>
      )}

      {progress && (
        <p className="mt-2 flex items-center gap-2 text-[#5a4a3a]">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> {progress}
        </p>
      )}

      {job?.status === 'review_ready' && !progress && (
        <div className="mt-2 space-y-3">
          {ready.map((s) => (
            <div key={s.sku}>
              <p className="text-[#241813]">
                <span className="font-semibold">{s.sku}</span>
                {s.entry?.title ? ` · ${s.entry.title}` : ''}
                <span className="text-[#8a7a6a]">
                  {' '}
                  · {s.uploads!.length} photo{s.uploads!.length === 1 ? '' : 's'}
                  {s.entry?.imageCount ? `, entry already has ${s.entry.imageCount}` : ''}
                </span>
              </p>
              {(() => {
                const m = matched.find((x) => x.sku === s.sku);
                return m?.folderName ? (
                  <p className="text-[11.5px] text-[#8a7a6a]">
                    From folder “{m.folderName}”{m.otherFolders.length ? ` (also found: ${m.otherFolders.slice(0, 3).join(', ')})` : ''}
                  </p>
                ) : null;
              })()}
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {s.uploads!.map((u, i) => (
                  <a key={u} href={u} target="_blank" rel="noreferrer" className="relative block">
                    <img src={u} alt="" style={{ width: thumb, height: thumb }} className="rounded-md object-cover border border-[#e6dccb] bg-white" />
                    <span className="absolute bottom-0.5 left-0.5 rounded bg-black/60 px-1 text-[10px] text-white">{i + 1}</span>
                  </a>
                ))}
              </div>
              {s.review && (
                <div className={cn('mt-1.5 text-[12px]', s.review.matches === 'no' ? 'text-red-700' : s.review.issues.length ? 'text-amber-800' : 'text-emerald-700')}>
                  <p className="flex items-start gap-1">
                    {s.review.matches === 'no' || s.review.issues.length ? <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" /> : <CheckCircle2 className="w-3.5 h-3.5 mt-px shrink-0" />}
                    <span>Claire: {s.review.note}</span>
                  </p>
                  {s.review.issues.length > 0 && (
                    <ul className="ml-5 list-disc text-[#6a5a4a]">
                      {s.review.issues.map((x) => (
                        <li key={x}>{x}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          ))}
          {hasExisting && (
            <div className="flex flex-wrap gap-3 text-[12.5px] text-[#5a4a3a]">
              <label className="inline-flex items-center gap-1.5">
                <input type="radio" checked={mode === 'add'} onChange={() => setMode('add')} /> Add after the existing photos
              </label>
              <label className="inline-flex items-center gap-1.5">
                <input type="radio" checked={mode === 'replace'} onChange={() => setMode('replace')} /> Replace the existing photos
              </label>
            </div>
          )}
          <div className="flex gap-2">
            <button onClick={() => finish('attach')} disabled={!ready.length} className={primary}>
              <Check className="w-3.5 h-3.5" /> Add to {ready.length === 1 ? 'product entry' : `${ready.length} product entries`}
              {job.thenSubmit ? ' and send to review' : ''}
            </button>
            <button onClick={() => finish('cancel')} className={secondary}>
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
          </div>
        </div>
      )}

      {job?.status === 'done' && (
        <ul className="mt-1.5 space-y-0.5 text-emerald-700">
          {job.skus
            .filter((s) => s.result)
            .map((s) => (
              <li key={s.sku} className="flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" /> {s.sku}: {job.mode === 'replace' ? `replaced, ${s.result!.total}` : `added ${s.result!.added}, ${s.result!.total} total`}
                {s.submitted?.ok && ` · sent to ${s.submitted.status === 'final_review' ? 'Final Review' : 'Pending Review'}`}
                {s.submitted && !s.submitted.ok && <span className="text-amber-800"> · not sent to review: {s.submitted.reason}</span>}
              </li>
            ))}
        </ul>
      )}
      {job?.status === 'saving' && !progress && <p className="mt-1.5 text-[#8a7a6a]">Saving the photos…</p>}
      {job?.status === 'cancelled' && <p className="mt-1.5 text-[#8a7a6a]">Cancelled. Nothing was saved.</p>}

      {job && job.status !== 'done' && job.status !== 'cancelled' && (notFound.length > 0 || noEntry.length > 0 || skipped.length > 0) && (
        <div className="mt-2 space-y-0.5 text-[12px] text-[#8a7a6a]">
          {notFound.length > 0 && <p>No folder found for: {notFound.join(', ')}</p>}
          {noEntry.length > 0 && <p>No product entry yet for: {noEntry.join(', ')}</p>}
          {skipped.length > 0 && <p>Skipped (format not supported or over 24 photos): {skipped.slice(0, 6).join(', ')}{skipped.length > 6 ? '…' : ''}</p>}
        </div>
      )}
      {error && <p className="mt-1.5 text-[12px] text-red-700">{error}</p>}
    </div>
  );
};
